import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Card, StatusPill, Modal, Alert, Empty, Spinner } from '../components/ui';

/**
 * Adopted resources: hand-built flows, channels and machines the hub did not
 * make, which the account owner has tagged in AWS so the hub may start and
 * stop them. Administrators only.
 *
 * There are no edit or delete controls on this page and there must never be:
 * the server has no routes for them and IAM would refuse them. "Release" only
 * takes the row off this page.
 */

type Kind = 'machine' | 'flow' | 'channel';

interface AdoptedRow {
  id: string;
  kind: Kind;
  resourceId: string;
  region: string;
  name: string;
  /** running | stopped | starting | stopping | missing | unknown, or AWS's own word. */
  state: string;
  detail: string | null;
  /** Still carries the tag in AWS. Null when that could not be read. */
  tagged: boolean | null;
  adoptedBy: string | null;
  adoptedAt: string;
}

interface Candidate {
  kind: Kind;
  resourceId: string;
  region: string;
  name: string;
  state: string;
  detail: string | null;
}

interface Payload {
  tag: { key: string; value: string };
  regions: string[];
  adopted: AdoptedRow[];
  adoptable: Candidate[];
  notes: string[];
}

const KINDS: { kind: Kind; title: string; one: string }[] = [
  { kind: 'flow', title: 'Flows', one: 'flow' },
  { kind: 'channel', title: 'Channels', one: 'channel' },
  { kind: 'machine', title: 'Machines', one: 'machine' },
];

// What stopping each kind does to whatever is relying on it - said before the
// button does it, because these are production.
const STOP_WARNING: Record<Kind, string> = {
  flow: 'Anything this flow is carrying goes off air, for every receiver, until it is started again.',
  channel: 'The channel stops encoding and everything downstream of it loses its feed until it is started again.',
  machine: 'The machine shuts down. Anything running on it stops, and a public address that is not a static one will be different when it starts again.',
};

/** StatusPill speaks EC2; "starting" is its "pending". */
const pillState = (state: string) => (state === 'starting' ? 'pending' : state);

function Badge() {
  return (
    <span
      className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-secondary ring-1 ring-hairline"
      title="The hub can start and stop this. It cannot change or delete it."
    >
      adopted - protected
    </span>
  );
}

export function Adopted() {
  const { user } = useAuth();
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [stopping, setStopping] = useState<AdoptedRow | null>(null);
  const [releasing, setReleasing] = useState<AdoptedRow | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await api.get<Payload>('/adopted'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load adopted resources.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.isAdmin) return;
    void load();
    // Each refresh is a handful of AWS calls per region, so no faster than the
    // Machines page.
    const timer = setInterval(() => void load(), 15_000);
    return () => clearInterval(timer);
  }, [load, user?.isAdmin]);

  async function act(key: string, call: () => Promise<unknown>, failed: string): Promise<void> {
    setBusy(key);
    setError(null);
    try {
      await call();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : failed);
    } finally {
      setBusy(null);
      setStopping(null);
      setReleasing(null);
    }
  }

  // The server refuses everyone else as well; this only spares them the error.
  if (!user?.isAdmin) {
    return <Alert tone="info" title="Administrators only">Adopted resources can only be seen and controlled by an administrator.</Alert>;
  }
  if (loading) return <Spinner label="Loading adopted resources…" />;

  const adopted = data?.adopted ?? [];
  const adoptable = data?.adoptable ?? [];
  const tag = data?.tag ?? { key: 'cloudprod:adopted', value: 'true' };
  const region = data?.regions[0] ?? 'ap-southeast-2';

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Adopted resources</h1>
        <p className="text-sm text-ink-muted">
          Flows, channels and instances that were built by hand, not by the hub. Once adopted, the hub can start and
          stop them - and nothing else. It never changes or deletes them.
        </p>
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {data?.notes.map((n) => <Alert key={n} tone="warning">{n}</Alert>)}

      <Card>
        {adopted.length === 0 ? (
          <Empty title="Nothing adopted yet">Tag a resource in AWS as described below, then adopt it here.</Empty>
        ) : (
          <>
          <ImportBar onDone={() => void load()} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grid text-left text-xs text-ink-muted">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Region</th>
                  <th className="px-4 py-2 font-medium">State</th>
                  <th className="px-4 py-2 font-medium">Adopted</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              {KINDS.map(({ kind, title }) => {
                const rows = adopted.filter((r) => r.kind === kind);
                if (rows.length === 0) return null;
                return (
                  <tbody key={kind}>
                    <tr className="border-b border-grid bg-raised/40">
                      <td colSpan={5} className="px-4 py-1.5 text-xs font-medium text-ink-secondary">{title}</td>
                    </tr>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-grid last:border-0 hover:bg-raised/40">
                        <td className="px-4 py-2.5">
                          <span className="font-medium">{r.name}</span>
                          <Badge />
                          <div className="font-mono text-[11px] text-ink-muted">
                            {r.kind === 'machine' ? r.resourceId : r.resourceId.split(':').slice(5).join(':')}
                            {r.detail && ` · ${r.detail}`}
                          </div>
                          {r.tagged === false && r.state !== 'missing' && (
                            <div className="mt-1 text-xs" style={{ color: 'var(--color-warning)' }}>
                              The tag {tag.key} = {tag.value} is no longer on this in AWS, so AWS will refuse to start or
                              stop it. Put the tag back, or release it.
                            </div>
                          )}
                          {r.state === 'missing' && (
                            <div className="mt-1 text-xs" style={{ color: 'var(--color-warning)' }}>
                              AWS no longer has this. It was deleted outside the hub; release it to tidy up.
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-ink-secondary">{r.region}</td>
                        <td className="px-4 py-2.5"><StatusPill state={pillState(r.state)} /></td>
                        <td className="px-4 py-2.5 text-xs text-ink-secondary">
                          {new Date(`${r.adoptedAt.replace(' ', 'T')}Z`).toLocaleDateString()}
                          {r.adoptedBy && <div className="text-[11px] text-ink-muted">{r.adoptedBy}</div>}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex justify-end gap-1">
                            {r.state === 'stopped' && (
                              <Button
                                variant="primary"
                                loading={busy === r.id}
                                onClick={() => void act(r.id, () => api.post(`/adopted/${r.id}/start`), 'Could not start it.')}
                              >
                                Start
                              </Button>
                            )}
                            {r.state === 'running' && (
                              <Button loading={busy === r.id} onClick={() => setStopping(r)}>Stop</Button>
                            )}
                            <Button variant="ghost" disabled={busy === r.id} onClick={() => setReleasing(r)}>Release</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                );
              })}
            </table>
          </div>
          </>
        )}
      </Card>

      <div>
        <h2 className="text-sm font-semibold">Ready to adopt</h2>
        <p className="text-sm text-ink-muted">
          Found in AWS carrying the tag <code className="font-mono">{tag.key}</code> ={' '}
          <code className="font-mono">{tag.value}</code>, in {data?.regions.join(', ')}.
        </p>
      </div>

      <Card>
        {adoptable.length === 0 ? (
          <Empty title="Nothing is waiting">Nothing else in AWS carries the tag.</Empty>
        ) : (
          <div className="divide-y divide-grid">
            {adoptable.map((c) => {
              const key = `${c.kind}|${c.resourceId}`;
              return (
                <div key={key} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium">{c.name}</span>
                    <span className="ml-2 text-xs text-ink-muted">
                      {KINDS.find((k) => k.kind === c.kind)?.one} · {c.region}{c.detail && ` · ${c.detail}`}
                    </span>
                    <div className="truncate font-mono text-[11px] text-ink-muted">{c.resourceId}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <StatusPill state={pillState(c.state)} />
                    <Button
                      variant="primary"
                      loading={busy === key}
                      onClick={() => void act(key, () => api.post('/adopted', { kind: c.kind, resourceId: c.resourceId, region: c.region }), 'Could not adopt it.')}
                    >
                      Adopt
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card>
        <div className="space-y-3 px-4 py-3 text-sm text-ink-secondary">
          <div className="font-medium text-ink">How to make something adoptable</div>
          <p>
            Add this tag to it in AWS, exactly as written. The hub cannot add it - that is deliberate: only someone
            with their own AWS access decides what the hub may reach. It appears above within a minute or so.
          </p>
          <table className="text-xs">
            <tbody>
              <tr><td className="pr-4 text-ink-muted">Key</td><td className="font-mono text-ink">{tag.key}</td></tr>
              <tr><td className="pr-4 text-ink-muted">Value</td><td className="font-mono text-ink">{tag.value}</td></tr>
            </tbody>
          </table>
          <div className="space-y-2">
            <Example title="A MediaConnect flow">
              {`aws mediaconnect tag-resource --region ${region} --resource-arn <FLOW_ARN> --tags ${tag.key}=${tag.value}`}
            </Example>
            <Example title="A MediaLive channel">
              {`aws medialive create-tags --region ${region} --resource-arn <CHANNEL_ARN> --tags ${tag.key}=${tag.value}`}
            </Example>
            <Example title="An EC2 instance">
              {`aws ec2 create-tags --region ${region} --resources <INSTANCE_ID> --tags Key=${tag.key},Value=${tag.value}`}
            </Example>
          </div>
          <p>
            Taking the tag off again stops the hub being able to start or stop it, whatever this page says. Something
            Terraform manages elsewhere should get the tag in that Terraform, or the next apply will remove it.
          </p>
          <p className="text-xs text-ink-muted">
            Back to <Link to="/encoders" className="underline">Encoders &amp; flows</Link> or{' '}
            <Link to="/machines" className="underline">Machines</Link>.
          </p>
        </div>
      </Card>

      <Modal
        open={!!stopping}
        onClose={() => setStopping(null)}
        title={`Stop ${stopping?.name ?? ''}`}
        footer={
          <>
            <Button onClick={() => setStopping(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={busy === stopping?.id}
              onClick={() => stopping && void act(stopping.id, () => api.post(`/adopted/${stopping.id}/stop`), 'Could not stop it.')}
            >
              Stop it
            </Button>
          </>
        }
      >
        {stopping && (
          <Alert tone="warning" title="This is production, built outside the hub">{STOP_WARNING[stopping.kind]}</Alert>
        )}
      </Modal>

      <Modal
        open={!!releasing}
        onClose={() => setReleasing(null)}
        title={`Release ${releasing?.name ?? ''}`}
        footer={
          <>
            <Button onClick={() => setReleasing(null)}>Cancel</Button>
            <Button
              loading={busy === releasing?.id}
              onClick={() => releasing && void act(releasing.id, () => api.delete(`/adopted/${releasing.id}`), 'Could not release it.')}
            >
              Release
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-secondary">
          The hub stops listing it here. Nothing changes in AWS: it keeps running or stays stopped, exactly as it is,
          and it keeps its tag - so it goes back to "Ready to adopt" until the tag is taken off in AWS.
        </p>
      </Modal>
    </div>
  );
}

function Example({ title, children }: { title: string; children: string }) {
  return (
    <div>
      <div className="text-xs text-ink-muted">{title}</div>
      <pre className="overflow-x-auto rounded bg-raised px-2 py-1.5 font-mono text-[11px] text-ink">{children}</pre>
    </div>
  );
}

/** Every adopted channel and flow onto the Encoders and Flows pages, as the hub's own. */
function ImportBar({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ imported: string[]; failed: { name: string; message: string }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="space-y-2 border-b border-grid px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-ink-secondary">
          Bring every adopted channel and flow into the hub as its own: on the Encoders and Flows pages, with full control -
          edit, stop, start, delete. Nothing in AWS is changed by doing this.
        </span>
        <Button variant="primary" loading={busy} onClick={async () => {
          setBusy(true); setError(null);
          try { setResult(await api.post('/adopted/import')); onDone(); }
          catch (err) { setError(err instanceof Error ? err.message : 'That did not work.'); }
          finally { setBusy(false); }
        }}>Bring into the hub</Button>
      </div>
      {error && <div style={{ color: 'var(--color-critical)' }}>{error}</div>}
      {result && (
        <div className="text-xs text-ink-secondary">
          {result.imported.length} brought in{result.failed.length ? `, ${result.failed.length} not:` : '.'}
          {result.failed.map((f) => <div key={f.name}>{f.name}: {f.message}</div>)}
        </div>
      )}
    </div>
  );
}
